import { useState, useEffect } from 'react';
import { useSiteContext } from '../contexts/SiteContext';
import {
  AlertCircle, Loader, ShoppingCart, DollarSign, UserX,
  RefreshCw, ChevronDown, ChevronUp, Package, TrendingDown,
  Mail, Clock, User
} from 'lucide-react';

const AbandonedCarts = ({ user }) => {
  const { selectedSite } = useSiteContext();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [dateRange, setDateRange] = useState('7d');
  const [expandedCart, setExpandedCart] = useState(null);

  useEffect(() => {
    if (selectedSite) {
      fetchData();
    } else {
      setLoading(false);
      setError('Please select a site');
    }
  }, [selectedSite, dateRange]);

  const fetchData = async () => {
    if (!selectedSite) return;

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(
        `/app/api/analytics/abandoned-carts.php?siteId=${selectedSite.id}&dateRange=${dateRange}`,
        {
          headers: {
            'Authorization': `Bearer ${user.token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      const result = await response.json();

      if (response.ok && result.success) {
        setData(result.data);
      } else {
        setError(result.message || 'Failed to fetch abandoned cart data');
      }
    } catch (err) {
      console.error('Error:', err);
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(value);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTimeSince = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);

    if (diffDays > 0) {
      return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    } else if (diffHours > 0) {
      return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    } else {
      const diffMins = Math.floor(diffMs / (1000 * 60));
      return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    }
  };

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <Loader className="h-12 w-12 text-orange-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading abandoned carts...</p>
        </div>
      </div>
    );
  }

  if (!selectedSite) {
    return (
      <div className="p-6">
        <div className="rounded-lg bg-yellow-50 p-4 flex items-center gap-3">
          <AlertCircle className="h-5 w-5 text-yellow-600" />
          <p className="text-yellow-800">Please select a site</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8 flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
              <ShoppingCart className="h-8 w-8 text-orange-600" />
              Abandoned Carts
            </h1>
            <p className="text-gray-600 mt-1">
              Track and recover lost sales from abandoned shopping carts
            </p>
          </div>
          <div className="flex items-center gap-3">
            {/* Date Range */}
            <div className="flex rounded-lg border border-gray-300 overflow-hidden">
              {['7d', '30d', '90d'].map((range) => (
                <button
                  key={range}
                  onClick={() => setDateRange(range)}
                  className={`px-3 py-2 text-sm ${
                    dateRange === range
                      ? 'bg-orange-600 text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {range}
                </button>
              ))}
            </div>
            <button
              onClick={fetchData}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-6 rounded-lg bg-red-50 p-4 flex items-center gap-3">
            <AlertCircle className="h-5 w-5 text-red-600" />
            <p className="text-red-800">{error}</p>
          </div>
        )}

        {data && (
          <div className="space-y-6">
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-white rounded-lg shadow p-4">
                <div className="flex items-center gap-2 mb-2">
                  <ShoppingCart className="h-5 w-5 text-orange-600" />
                  <span className="text-sm text-gray-500">Abandoned Carts</span>
                </div>
                <p className="text-2xl font-bold text-gray-900">
                  {data.summary.total_carts.toLocaleString()}
                </p>
              </div>

              <div className="bg-white rounded-lg shadow p-4">
                <div className="flex items-center gap-2 mb-2">
                  <DollarSign className="h-5 w-5 text-red-600" />
                  <span className="text-sm text-gray-500">Lost Revenue</span>
                </div>
                <p className="text-2xl font-bold text-red-600">
                  {formatCurrency(data.summary.total_value)}
                </p>
              </div>

              <div className="bg-white rounded-lg shadow p-4">
                <div className="flex items-center gap-2 mb-2">
                  <TrendingDown className="h-5 w-5 text-purple-600" />
                  <span className="text-sm text-gray-500">Avg Cart Value</span>
                </div>
                <p className="text-2xl font-bold text-gray-900">
                  {formatCurrency(data.summary.avg_cart_value)}
                </p>
              </div>
            </div>

            {/* Charts Row */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Top Abandoned Products */}
              {data.top_products && data.top_products.length > 0 && (
                <div className="bg-white rounded-lg shadow p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <Package className="h-5 w-5 text-orange-600" />
                    Most Abandoned Products
                  </h3>
                  <div className="space-y-3">
                    {data.top_products.map((product, idx) => {
                      const maxCount = Math.max(...data.top_products.map(p => p.count));
                      const percentage = maxCount > 0 ? (product.count / maxCount) * 100 : 0;
                      return (
                        <div key={idx}>
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-sm font-medium text-gray-900 truncate max-w-[200px]" title={product.name}>
                              {product.name}
                            </span>
                            <div className="text-right">
                              <span className="text-sm font-semibold text-gray-900">{product.count}x</span>
                              <span className="text-xs text-gray-500 ml-2">
                                {formatCurrency(product.total_value)}
                              </span>
                            </div>
                          </div>
                          <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-orange-500 rounded-full"
                              style={{ width: `${percentage}%` }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Daily Trend */}
              {data.daily_trend && data.daily_trend.length > 0 && (
                <div className="bg-white rounded-lg shadow p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Abandonment Trend</h3>
                  <div className="overflow-x-auto">
                    <div className="min-w-[400px]">
                      {/* Simple bar chart */}
                      <div className="flex items-end gap-1 h-32">
                        {data.daily_trend.map((day, idx) => {
                          const maxCount = Math.max(...data.daily_trend.map(d => d.count));
                          const height = maxCount > 0 ? (day.count / maxCount) * 100 : 0;
                          return (
                            <div
                              key={idx}
                              className="flex-1 bg-orange-500 rounded-t hover:bg-orange-600 transition-colors group relative"
                              style={{ height: `${height}%`, minHeight: day.count > 0 ? '4px' : '0' }}
                              title={`${day.date}: ${day.count} carts (${formatCurrency(day.value)})`}
                            >
                              <div className="hidden group-hover:block absolute bottom-full left-1/2 -translate-x-1/2 mb-2 p-2 bg-gray-900 text-white text-xs rounded whitespace-nowrap z-10">
                                {day.date}<br />
                                {day.count} carts<br />
                                {formatCurrency(day.value)}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      <div className="flex justify-between mt-2 text-xs text-gray-500">
                        <span>{data.daily_trend[0]?.date}</span>
                        <span>{data.daily_trend[data.daily_trend.length - 1]?.date}</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Abandoned Carts List */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">Recent Abandoned Carts</h3>
                <p className="text-sm text-gray-500">Sorted by cart value (highest first)</p>
              </div>
              <div className="divide-y divide-gray-200">
                {data.carts && data.carts.length > 0 ? (
                  data.carts.slice(0, 20).map((cart, idx) => (
                    <div key={cart.id} className="hover:bg-gray-50">
                      <div
                        className="px-6 py-4 cursor-pointer"
                        onClick={() => setExpandedCart(expandedCart === cart.id ? null : cart.id)}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className={`p-2 rounded-full ${cart.is_guest ? 'bg-gray-100' : 'bg-blue-100'}`}>
                              {cart.is_guest ? (
                                <UserX className="h-5 w-5 text-gray-600" />
                              ) : (
                                <User className="h-5 w-5 text-blue-600" />
                              )}
                            </div>
                            <div>
                              <p className="font-medium text-gray-900">
                                {cart.customer_name}
                              </p>
                              {cart.customer_email && (
                                <p className="text-sm text-gray-500 flex items-center gap-1">
                                  <Mail className="h-3 w-3" />
                                  {cart.customer_email}
                                </p>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-6">
                            <div className="text-right">
                              <p className="font-semibold text-gray-900">
                                {formatCurrency(cart.grand_total, cart.currency)}
                              </p>
                              <p className="text-sm text-gray-500">
                                {cart.items_count} item{cart.items_count !== 1 ? 's' : ''}
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="text-sm text-gray-500 flex items-center gap-1">
                                <Clock className="h-3 w-3" />
                                {getTimeSince(cart.updated_at)}
                              </p>
                            </div>
                            {expandedCart === cart.id ? (
                              <ChevronUp className="h-5 w-5 text-gray-400" />
                            ) : (
                              <ChevronDown className="h-5 w-5 text-gray-400" />
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Expanded cart items */}
                      {expandedCart === cart.id && cart.items && cart.items.length > 0 && (
                        <div className="px-6 pb-4 bg-gray-50">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="text-left text-gray-500 border-b border-gray-200">
                                <th className="pb-2">Product</th>
                                <th className="pb-2 text-right">SKU</th>
                                <th className="pb-2 text-right">Qty</th>
                                <th className="pb-2 text-right">Price</th>
                                <th className="pb-2 text-right">Total</th>
                              </tr>
                            </thead>
                            <tbody>
                              {cart.items.map((item, itemIdx) => (
                                <tr key={itemIdx} className="border-b border-gray-100 last:border-0">
                                  <td className="py-2 font-medium text-gray-900">{item.name}</td>
                                  <td className="py-2 text-right text-gray-500">{item.sku}</td>
                                  <td className="py-2 text-right">{item.qty}</td>
                                  <td className="py-2 text-right">{formatCurrency(item.price, cart.currency)}</td>
                                  <td className="py-2 text-right font-medium">{formatCurrency(item.row_total, cart.currency)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  ))
                ) : (
                  <div className="px-6 py-8 text-center text-gray-500">
                    <ShoppingCart className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                    <p>No abandoned carts found in this period</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AbandonedCarts;
